import { supabase } from './supabaseClient';
import { callEdgeFunctionWithAuthRefresh } from './auth-utils';
import { AuthErrorInterceptor } from './auth-error-interceptor';
import { FunctionsFetchError, FunctionInvokeOptions } from '@supabase/supabase-js';
import { systemLogger } from '@/lib/logging';
import { logErrorRemotely } from './remote-error-logger';

/**
 * Global wrapper for Supabase Edge Function calls with automatic auth error handling
 * This should be used instead of direct supabase.functions.invoke calls
 */

export interface EdgeFunctionOptions {
  body?: unknown;
  headers?: Record<string, string>;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
}

// Type alias for compatibility with Supabase's FunctionInvokeOptions
export type CompatibleEdgeFunctionOptions = FunctionInvokeOptions & EdgeFunctionOptions;

export interface EdgeFunctionResponse<T = unknown> {
  data: T | null;
  error: unknown;
}

/**
 * Enhanced edge function caller that automatically handles auth errors
 * and integrates with the global auth error handling system
 */
export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  options?: CompatibleEdgeFunctionOptions,
  authErrorHandler?: (_error: unknown) => void
): Promise<EdgeFunctionResponse<T>> {
  const callId = Math.random().toString(36).substring(7);

  try {
    systemLogger.debug(`🌐 [WRAPPER-${callId}] invokeEdgeFunction called for '${functionName}'`, {
      hasOptions: !!options,
      hasAuthHandler: !!authErrorHandler,
      timestamp: Date.now()
    });

    // Use our enhanced auth-aware wrapper
    const result = await callEdgeFunctionWithAuthRefresh<T>(
      functionName,
      options ? {
        body: options.body as Record<string, unknown> | unknown[],
        ...(options.method ? { method: options.method } : {})
      } : undefined,
      authErrorHandler
    );

    systemLogger.debug(`🌐 [WRAPPER-${callId}] callEdgeFunctionWithAuthRefresh completed for '${functionName}'`, {
      hasData: !!result.data,
      hasError: !!result.error,
      errorType: result.error ? (result.error as Error).message?.substring(0, 50) : 'NO_ERROR'
    });

    // If we get an error, do additional analysis for Edge Function specific patterns
    if (result.error) {
      systemLogger.warn('Warning:', result.error);

      // Log edge function errors remotely with specific function name
      const error = result.error as Error & { details?: string; code?: string; status?: number };
      logErrorRemotely({
        errorType: 'edge_function_error',
        errorMessage: error.message || 'Edge function failed',
        errorStack: error.stack,
        operationName: functionName,
        operationPayload: options?.body
      }).catch((_logError) => {
        // Silent fail - don't break app if error logging fails
        systemLogger.warn('Failed to log edge function error remotely');
      });

      // Special handling for "missing sub claim" errors
      const errorWithMessage = result.error as Error & { details?: string; code?: string };
      if (errorWithMessage.message?.includes('missing sub claim') || errorWithMessage.details?.includes('missing sub claim')) {
        systemLogger.error(`[SupabaseFunctionWrapper] Missing sub claim detected for '${functionName}' - JWT token corruption`);
        errorWithMessage.code = 'JWT_SUB_CLAIM_MISSING';
      }

      // Add specific logging for Edge Runtime JWT rejections
      if (result.error instanceof FunctionsFetchError) {
        systemLogger.error('Failed operation:', result.error);
      }

      // Use enhanced Supabase-specific error analysis
      const errorDetails = await AuthErrorInterceptor.analyzeSupabaseError(result.error);

      if (errorDetails) {
        systemLogger.warn(`[SupabaseFunctionWrapper] Auth error detected: ${errorDetails.type}`);

        // For critical auth errors, notify the handler immediately
        if (errorDetails.shouldRedirectToLogin && authErrorHandler) {
          // Notifying auth error handler
          authErrorHandler(result.error);
        }
      }
    }

    return result;
  } catch (error: unknown) {
    systemLogger.error('Failed operation:', error);
    
    // Even caught exceptions might be auth-related - use enhanced analysis
    const errorDetails = await AuthErrorInterceptor.analyzeSupabaseError(error);
    if (errorDetails && authErrorHandler) {
      authErrorHandler(error);
    }
    
    return {
      data: null,
      error: error
    };
  }
}

/**
 * Convenience function that uses a default auth error handler
 * This should be the most common way to call Edge Functions
 */
export async function invokeEdgeFunctionSafely<T = unknown>(
  functionName: string,
  options?: CompatibleEdgeFunctionOptions
): Promise<EdgeFunctionResponse<T>> {
  const callId = Math.random().toString(36).substring(7);

  systemLogger.debug(`🛡️ [SAFELY-${callId}] invokeEdgeFunctionSafely called for '${functionName}'`, {
    hasOptions: !!options,
    payload: options?.body,
    timestamp: Date.now()
  });

  // We'll get the auth error handler from the context at runtime
  // For now, we'll use a placeholder that can be set up later
  const authErrorHandler = (window as { __globalAuthErrorHandler?: (_error: unknown) => void }).__globalAuthErrorHandler;

  const result = await invokeEdgeFunction<T>(functionName, options, authErrorHandler);

  systemLogger.debug(`🛡️ [SAFELY-${callId}] invokeEdgeFunctionSafely completed for '${functionName}'`, {
    hasData: !!result.data,
    hasError: !!result.error,
    hasGlobalHandler: !!authErrorHandler
  });

  return result;
}

/**
 * Direct fallback to original supabase.functions.invoke for cases where
 * we explicitly don't want auth error handling (rare cases)
 */
export async function invokeEdgeFunctionDirect<T = unknown>(
  functionName: string,
  options?: CompatibleEdgeFunctionOptions
): Promise<EdgeFunctionResponse<T>> {
  systemLogger.warn(`[SupabaseFunctionWrapper] Using direct call for '${functionName}' - bypassing auth error handling`);

  const { data, error } = await supabase.functions.invoke<T>(functionName, options as FunctionInvokeOptions);

  return { data, error };
}

/**
 * Set up global auth error handler
 * This should be called once during app initialization
 */
export function setupGlobalAuthErrorHandler(handler: (_error: unknown) => void) {
  (window as { __globalAuthErrorHandler?: (_error: unknown) => void }).__globalAuthErrorHandler = handler;
  // Global auth error handler configured
}

/**
 * Legacy compatibility - gradually migrate from this
 * @deprecated Use invokeEdgeFunctionSafely instead
 */
export const callEdgeFunction = invokeEdgeFunctionSafely;

/**
 * Type-safe wrapper with better error handling for common operations
 */
export class EdgeFunctionClient {
  private authErrorHandler: ((_error: unknown) => void) | undefined;

  constructor(authErrorHandler?: (_error: unknown) => void) {
    this.authErrorHandler = authErrorHandler;
  }

  async call<T = unknown>(
    functionName: string,
    options?: CompatibleEdgeFunctionOptions
  ): Promise<T> {
    const result = await invokeEdgeFunction<T>(
      functionName, 
      options, 
      this.authErrorHandler
    );

    if (result.error) {
      // Preserve the original error structure but add context
      const errorWithMessage = result.error as Error;
      const enhancedError = new Error(
        errorWithMessage.message || `Edge Function '${functionName}' failed`
      );
      
      // Preserve important error properties
      Object.assign(enhancedError, result.error);
      
      throw enhancedError;
    }

    if (result.data === null) {
      systemLogger.warn(`[EdgeFunctionClient] '${functionName}' returned null data`);
    }

    return result.data as T;
  }

  async callSafe<T = unknown>(
    functionName: string,
    defaultValue: T,
    options?: CompatibleEdgeFunctionOptions
  ): Promise<T> {
    try {
      return await this.call<T>(functionName, options);
    } catch (_error) {
      systemLogger.error('Failed operation:', _error);
      return defaultValue;
    }
  }
}

/**
 * Factory function to create an Edge Function client with auth handling
 */
export function createEdgeFunctionClient(authErrorHandler?: (_error: unknown) => void): EdgeFunctionClient {
  return new EdgeFunctionClient(authErrorHandler);
}